(() => {
  const SELECTOR = '[data-calinfi-product-materials]';

  class CalinfiProductMaterials {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-calinfi-material-track]');
      this.slides = Array.from(root.querySelectorAll('[data-calinfi-material-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-calinfi-material-dot]'));
      this.heading = root.querySelector('[data-calinfi-material-heading]');
      this.body = root.querySelector('[data-calinfi-material-body]');
      this.images = Array.from(root.querySelectorAll('[data-calinfi-material-image]'));
      this.prevButton = root.querySelector('[data-calinfi-material-prev]');
      this.nextButton = root.querySelector('[data-calinfi-material-next]');
      this.stickyPurchase = root.querySelector('[data-calinfi-sticky-purchase]');
      this.stickyLabel = root.querySelector('[data-calinfi-sticky-label]');
      this.stickyTotal = root.querySelector('[data-calinfi-sticky-total]');
      this.stickyBuyButton = root.querySelector('[data-calinfi-sticky-buy]');
      this.stickyAddButton = root.querySelector('[data-calinfi-sticky-add]');
      this.stickyToggleButton = root.querySelector('[data-calinfi-sticky-toggle]');
      this.stickyCart = root.querySelector('[data-calinfi-sticky-cart]');
      this.stickyCartClose = root.querySelector('[data-calinfi-sticky-cart-close]');
      this.stickyCartItems = root.querySelector('[data-calinfi-sticky-cart-items]');
      this.purchaseState = null;
      this.index = 0;
      this.resizeFrame = null;
      this.scrollFrame = null;
      this.stickyCartScrollFrame = null;
      this.purchaseObserver = null;
    }

    init() {
      if (!this.track || !this.slides.length || this.root.dataset.calinfiMaterialsInitialized === 'true') return;

      this.root.dataset.calinfiMaterialsInitialized = 'true';
      this.prevButton?.addEventListener('click', () => this.show(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.show(Number(dot.dataset.calinfiMaterialDot || 0)));
      });
      this.images.forEach((image) => {
        image.addEventListener('error', () => {
          image.closest('[data-calinfi-material-slide]')?.classList.add('is-missing-image');
        });
      });
      window.addEventListener('resize', () => this.requestLayout());

      this.show(0);
      this.initStickyPurchase();
    }

    initStickyPurchase() {
      if (!this.stickyPurchase || this.stickyPurchase.dataset.calinfiStickyPurchaseInitialized === 'true') return;

      this.stickyPurchase.dataset.calinfiStickyPurchaseInitialized = 'true';

      const sourceLabel = document.querySelector('.calinfi-product-hero__total-label');
      const sourceTotal = document.querySelector('[data-calinfi-total]');
      const sourceBuy = document.querySelector('[data-calinfi-buy-now]');
      const sourceAdd = document.querySelector('[data-calinfi-add-to-cart]');
      const sourcePurchase = document.querySelector('.calinfi-product-hero__purchase');

      const syncButton = (target, source) => {
        if (!target || !source) return;

        const isDisabled = source.disabled || source.getAttribute('aria-disabled') === 'true';
        target.disabled = isDisabled;
        target.toggleAttribute('aria-disabled', isDisabled);
        target.classList.toggle('is-loading', source.classList.contains('is-loading'));
      };

      const syncPurchase = () => {
        if (this.stickyLabel && sourceLabel) this.stickyLabel.textContent = sourceLabel.textContent;
        if (this.stickyTotal && sourceTotal) this.stickyTotal.textContent = sourceTotal.textContent;
        syncButton(this.stickyBuyButton, sourceBuy);
        syncButton(this.stickyAddButton, sourceAdd);
      };

      this.stickyBuyButton?.addEventListener('click', () => sourceBuy?.click());
      this.stickyAddButton?.addEventListener('click', () => sourceAdd?.click());
      this.stickyToggleButton?.addEventListener('click', () => this.setStickyCartOpen(!this.stickyPurchase.classList.contains('is-open')));
      this.stickyCartClose?.addEventListener('click', () => this.setStickyCartOpen(false));
      this.stickyCartItems?.addEventListener('click', (event) => this.handleStickyCartClick(event));
      this.stickyCartItems?.addEventListener('scroll', () => this.requestStickyCartScrollbar(), { passive: true });
      document.addEventListener('calinfi:product-purchase:update', (event) => {
        this.purchaseState = event.detail || null;
        if (this.purchaseState?.label && this.stickyLabel) this.stickyLabel.textContent = this.purchaseState.label;
        if (this.purchaseState?.total && this.stickyTotal) this.stickyTotal.textContent = this.purchaseState.total;
        this.renderStickyCart();
      });

      if (window.MutationObserver) {
        this.purchaseObserver = new MutationObserver(syncPurchase);
        [sourceLabel, sourceTotal].forEach((node) => {
          if (!node) return;
          this.purchaseObserver.observe(node, { childList: true, characterData: true, subtree: true });
        });
        [sourceBuy, sourceAdd].forEach((node) => {
          if (!node) return;
          this.purchaseObserver.observe(node, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
          });
        });
      }

      this.sourcePurchase = sourcePurchase;
      syncPurchase();
      this.updateStickyVisibility();
      window.addEventListener('scroll', () => this.requestStickyVisibility(), { passive: true });
      window.addEventListener('resize', () => {
        this.requestStickyVisibility();
        this.requestStickyCartScrollbar();
      });
    }

    requestStickyVisibility() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateStickyVisibility();
      });
    }

    updateStickyVisibility() {
      if (!this.stickyPurchase) return;

      const rect = this.root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const sourceRect = this.sourcePurchase?.getBoundingClientRect();
      const sourceHasPassed = sourceRect ? sourceRect.bottom < viewportHeight - 24 : rect.top <= viewportHeight * 0.72;
      const pageIsProductDetail = Boolean(document.querySelector('[data-calinfi-product-hero]'));

      this.stickyPurchase.classList.toggle('is-visible', isDesktop && pageIsProductDetail && sourceHasPassed);
    }

    setStickyCartOpen(isOpen) {
      if (!this.stickyPurchase || !this.stickyCart) return;

      this.stickyPurchase.classList.toggle('is-open', isOpen);
      this.stickyToggleButton?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      this.stickyCart.hidden = !isOpen;
      if (isOpen) {
        this.renderStickyCart();
        this.requestStickyCartScrollbar();
      }
    }

    renderStickyCart() {
      if (!this.stickyCartItems) return;

      const product = this.purchaseState?.product || null;
      const accessories = this.purchaseState?.accessories || [];
      const productMarkup =
        product?.id && product.title
          ? `
            <article class="calinfi-product-materials__sticky-cart-item calinfi-product-materials__sticky-cart-item--product" data-sticky-product>
              <div class="calinfi-product-materials__sticky-cart-media" aria-hidden="true">
                ${
                  product.image
                    ? `<img src="${product.image}" alt="" loading="lazy">`
                    : '<span class="calinfi-product-materials__sticky-cart-placeholder"></span>'
                }
              </div>
              <div class="calinfi-product-materials__sticky-cart-copy">
                <p class="calinfi-product-materials__sticky-cart-title">${this.escapeHtml(product.title)}</p>
                <p class="calinfi-product-materials__sticky-cart-meta">Current stove</p>
              </div>
              <p class="calinfi-product-materials__sticky-cart-price calinfi-product-materials__sticky-cart-line-price">${this.formatStickyMoney(product.price)}</p>
            </article>
          `
          : '';

      if (!productMarkup && !accessories.length) {
        this.stickyCartItems.innerHTML = '<p class="calinfi-product-materials__sticky-cart-empty">No kit selected yet.</p>';
        this.requestStickyCartScrollbar();
        return;
      }

      const accessoryMarkup = accessories
        .map(
          (item) => `
            <article class="calinfi-product-materials__sticky-cart-item" data-sticky-variant-id="${item.id}">
              <div class="calinfi-product-materials__sticky-cart-media" aria-hidden="true">
                ${
                  item.image
                    ? `<img src="${item.image}" alt="" loading="lazy">`
                    : '<span class="calinfi-product-materials__sticky-cart-placeholder"></span>'
                }
              </div>
              <div class="calinfi-product-materials__sticky-cart-copy">
                <p class="calinfi-product-materials__sticky-cart-title">${this.escapeHtml(item.title || 'Accessory')}</p>
                ${
                  item.description
                    ? `<p class="calinfi-product-materials__sticky-cart-description">${this.escapeHtml(item.description)}</p>`
                    : ''
                }
              </div>
              <p class="calinfi-product-materials__sticky-cart-price calinfi-product-materials__sticky-cart-line-price">${this.formatStickyMoney(item.price)}</p>
              <div class="calinfi-product-materials__sticky-cart-controls" aria-label="${this.escapeHtml(item.title || 'Accessory')} quantity">
                <button type="button" data-sticky-action="plus" aria-label="Increase ${this.escapeHtml(item.title || 'Accessory')}">+</button>
                <span>${item.quantity}</span>
                <button type="button" data-sticky-action="minus" aria-label="Decrease ${this.escapeHtml(item.title || 'Accessory')}">&minus;</button>
                <button type="button" data-sticky-action="remove" aria-label="Remove ${this.escapeHtml(item.title || 'Accessory')}">
                  <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <path d="M11.4609 2.5L10.8428 10.5303C10.7821 11.3191 10.738 11.8818 10.6689 12.3223C10.6008 12.7568 10.5126 13.0321 10.3848 13.2529C10.1334 13.6869 9.75763 14.0355 9.30566 14.2529C9.07572 14.3635 8.79391 14.4302 8.35547 14.4648C7.91095 14.4999 7.34601 14.5 6.55469 14.5H6.44531C5.65399 14.5 5.08905 14.4999 4.64453 14.4648C4.20609 14.4302 3.92428 14.3635 3.69434 14.2529C3.24237 14.0355 2.86664 13.6869 2.61523 13.2529C2.48736 13.0321 2.39922 12.7568 2.33105 12.3223C2.26196 11.8818 2.2179 11.3191 2.15723 10.5303L1.53906 2.5H11.4609Z" stroke="currentColor"/>
                    <rect x="0.5" y="0.5" width="12" height="2" rx="0.5" stroke="currentColor"/>
                    <path d="M5 12L4.5 4" stroke="currentColor"/>
                    <path d="M8 12L8.5 4" stroke="currentColor"/>
                  </svg>
                </button>
              </div>
            </article>
          `
        )
        .join('');

      this.stickyCartItems.innerHTML = `${productMarkup}${accessoryMarkup}`;
      this.requestStickyCartScrollbar();
    }

    requestStickyCartScrollbar() {
      if (this.stickyCartScrollFrame) return;

      this.stickyCartScrollFrame = window.requestAnimationFrame(() => {
        this.stickyCartScrollFrame = null;
        this.updateStickyCartScrollbar();
      });
    }

    updateStickyCartScrollbar() {
      if (!this.stickyCart || !this.stickyCartItems || this.stickyCart.hidden) return;

      const scrollHeight = this.stickyCartItems.scrollHeight;
      const clientHeight = this.stickyCartItems.clientHeight;
      const hasOverflow = scrollHeight > clientHeight + 1;
      this.stickyCart.style.setProperty('--calinfi-sticky-scroll-opacity', hasOverflow ? '1' : '0');

      if (!hasOverflow) return;

      const trackHeight = Math.max(80, this.stickyCart.clientHeight - 120);
      const thumbHeight = Math.max(64, Math.min(trackHeight, (clientHeight / scrollHeight) * trackHeight));
      const maxScroll = scrollHeight - clientHeight;
      const maxThumbY = Math.max(0, trackHeight - thumbHeight);
      const thumbY = maxScroll > 0 ? (this.stickyCartItems.scrollTop / maxScroll) * maxThumbY : 0;

      this.stickyCart.style.setProperty('--calinfi-sticky-scroll-thumb-height', `${thumbHeight}px`);
      this.stickyCart.style.setProperty('--calinfi-sticky-scroll-thumb-y', `${thumbY}px`);
    }

    handleStickyCartClick(event) {
      const button = event.target.closest('[data-sticky-action]');
      if (!button) return;

      const item = button.closest('[data-sticky-variant-id]');
      const variantId = item?.dataset.stickyVariantId;
      const sourceCard = variantId ? document.querySelector(`[data-calinfi-accessory][data-variant-id="${variantId}"]`) : null;
      if (!sourceCard) return;

      const action = button.dataset.stickyAction;
      if (action === 'plus') {
        sourceCard.querySelector('[data-calinfi-accessory-plus]')?.click();
        return;
      }

      if (action === 'minus') {
        sourceCard.querySelector('[data-calinfi-accessory-minus]')?.click();
        return;
      }

      if (action === 'remove') {
        const quantity = Number(sourceCard.dataset.quantity || 0);
        for (let index = 0; index < quantity; index += 1) {
          sourceCard.querySelector('[data-calinfi-accessory-minus]')?.click();
        }
      }
    }

    formatStickyMoney(cents) {
      return `${Math.round(Number(cents || 0) / 100)}$`;
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    requestLayout() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => this.show(this.index));
    }

    show(nextIndex) {
      const normalizedIndex = (nextIndex + this.slides.length) % this.slides.length;
      const slide = this.slides[0];
      const style = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(style.columnGap || style.gap || 0) || 0;
      const media = this.track.closest('.calinfi-product-materials__media');
      const requestedOffset = normalizedIndex * (slide.getBoundingClientRect().width + gap);
      const maxOffset = media ? Math.max(0, this.track.scrollWidth - media.clientWidth) : requestedOffset;
      const offset = Math.min(requestedOffset, maxOffset);

      this.index = normalizedIndex;
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (this.heading) this.heading.textContent = this.slides[normalizedIndex]?.dataset.title || '';
      if (this.body) {
        const body = this.slides[normalizedIndex]?.dataset.body || '';
        this.body.textContent = body;
        this.body.hidden = !body;
      }

      this.slides.forEach((item, index) => {
        item.toggleAttribute('aria-hidden', index !== normalizedIndex);
      });

      this.dots.forEach((dot, index) => {
        const isActive = index === normalizedIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  const init = (container = document) => {
    container.querySelectorAll(SELECTOR).forEach((root) => new CalinfiProductMaterials(root).init());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      document.dispatchEvent(new CustomEvent('calinfi:product-purchase:request'));
    });
  } else {
    init();
    document.dispatchEvent(new CustomEvent('calinfi:product-purchase:request'));
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
